import React, { useEffect, useMemo, useState } from "react";
import { ClientPokemon } from "@/lib/battle/entities/client-pokemon";
import { CompleteBattleLog } from "types/battle-engine";
import { S3_BASE_URL } from "@/utils/s3";
import {
  Battle_Action_UseSkill,
  Direction,
  SkillData,
} from "types/battle-engine/battle";
import { MS_PER_TICK } from "types/battle-engine/constants";
import cn from "classnames";
import { StrokedText } from "../ui/text/stroked-text";
import { capitalizeFirstLetter } from "utils/string-utils";
import { parsePokeNameForSprite } from "utils/pokemon/name-utils";
import { getPokeData } from "game/data/pokemon/evolution";
import { PokemonSkillCard } from "../pokemon/pokemon-skill-card";

interface PokemonBattleHUDProps {
  pokemon: ClientPokemon;
  battleLog: CompleteBattleLog | null;
  currentTick: number;
  onClose: () => void;
  onNext?: () => void;
  onPrevious?: () => void;
}

interface SkillCooldownInfo {
  skill: SkillData;
  isOnCooldown: boolean;
  remainingTicks: number;
  remainingSeconds: number;
  totalCooldownTicks: number;
}

const colorsPerType: Record<string, { top: string; bottom: string }> = {
  fire: { top: "from-pk-red-5", bottom: "via-pk-red-3/80 to-pk-red-4" },
  water: { top: "from-pk-blue-5", bottom: "via-pk-blue-3/80 to-pk-blue-4" },
  grass: { top: "from-pk-green-5", bottom: "via-pk-green-3/80 to-pk-green-4" },
  electric: {
    top: "from-pk-yellow-3",
    bottom: "via-pk-yellow-3/80 to-pk-yellow-2",
  },
  psychic: {
    top: "from-pk-garnet-5",
    bottom: "via-pk-garnet-3/80 to-pk-garnet-4",
  },
  poison: {
    top: "from-pk-purple-5",
    bottom: "via-pk-purple-3/80 to-pk-purple-4",
  },
  ground: { top: "from-pk-beige-3", bottom: "via-pk-beige-3/80 to-pk-beige-4" },
  flying: { top: "from-pk-gray-5", bottom: "via-pk-gray-3/80 to-pk-gray-3" },
  bug: { top: "from-pk-green-5", bottom: "via-pk-green-3/80 to-pk-green-4" },
  rock: { top: "from-pk-gray-3", bottom: "via-pk-gray-3/80 to-pk-gray-3" },
  ice: { top: "from-pk-blue-5", bottom: "via-pk-blue-3/80 to-pk-blue-4" },
  dragon: { top: "from-pk-red-5", bottom: "via-pk-red-3/80 to-pk-red-4" },
  fairy: {
    top: "from-pk-garnet-5",
    bottom: "via-pk-garnet-3/80 to-pk-garnet-4",
  },
  dark: { top: "from-pk-gray-5", bottom: "via-pk-gray-3/80 to-pk-gray-4" },
  steel: { top: "from-pk-gray-5", bottom: "via-pk-gray-3/80 to-pk-gray-4" },
  ghost: { top: "from-pk-gray-2", bottom: "via-pk-gray-3/80 to-pk-purple-2" },
  fighting: { top: "from-pk-red-4", bottom: "via-pk-gray-3/80 to-pk-red-4" },
  normal: { top: "from-pk-gray-5", bottom: "via-pk-gray-3/80 to-pk-gray-4" },
};

const StatItem = ({
  label,
  value,
  color,
}: {
  label: string;
  value: number;
  color: string;
}) => (
  <div className="flex flex-col items-center min-w-[60px]">
    <span className="text-[10px] text-gray-400 uppercase tracking-wider">
      {label}
    </span>
    <span className={cn("text-sm font-bold", color)}>{value.toFixed(0)}</span>
  </div>
);

const SkillButton = ({
  info,
  onMouseEnter,
  onMouseLeave,
  onClick,
}: {
  info: SkillCooldownInfo;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  onClick?: () => void;
}) => {
  const typeName = info.skill.pokemon_type.toLocaleLowerCase();
  const colors = colorsPerType[typeName] || colorsPerType.normal;

  return (
    <div
      className="relative group h-16 rounded-md overflow-hidden border-3 border-pk-gray-2 cursor-pointer"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={onClick}
    >
      {/* Background Art */}
      <div
        className="absolute inset-0 bg-cover bg-center opacity-80 group-hover:opacity-100 transition-opacity"
        style={{
          backgroundImage: `url('/assets/card-art/${info.skill.id}.jpg')`,
        }}
      />

      {/* Gradient Overlays */}
      <div
        className={`absolute inset-0 bg-gradient-to-br ${colors?.top} to-transparent opacity-60 mix-blend-overlay z-5`}
      />
      <div
        className={`absolute inset-0 bg-gradient-to-tl from-transparent ${colors?.bottom} opacity-60 mix-blend-overlay z-5`}
      />

      {/* Skill Name (Truncated) */}
      <div className="absolute bottom-0 inset-x-0 bg-black/30 p-0.5 z-50">
        <div
          className="text-xs text-center text-white font-medium truncate leading-none px-1"
          style={{ textShadow: "2px 2px 0 #000" }}
        >
          {info.skill.name}
        </div>
      </div>

      {/* Cooldown Overlay */}
      {info.isOnCooldown && (
        <div className="absolute inset-0 bg-black/50 flex items-center justify-center backdrop-blur-[1px] z-10">
          <span className="text-lg font-bold text-white shadow-black drop-shadow-md">
            {info.remainingSeconds}
          </span>
        </div>
      )}

      {/* Tooltip */}
      <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-48 bg-gray-900/95 border border-gray-600 rounded p-2 text-xs hidden group-hover:block z-50 pointer-events-none shadow-xl">
        <div className="font-bold text-yellow-400 mb-1">{info.skill.name}</div>
        <div className="text-gray-300 leading-tight mb-2">
          {info.skill.description}
        </div>
        <div className="flex justify-between text-gray-500 text-[10px] uppercase">
          <span>
            CD: {((info.totalCooldownTicks * MS_PER_TICK) / 1000).toFixed(1)}s
          </span>
          <span>{info.skill.pokemon_type}</span>
        </div>
      </div>
    </div>
  );
};

export const PokemonBattleHUD: React.FC<PokemonBattleHUDProps> = ({
  pokemon,
  battleLog,
  currentTick,
  onClose,
  onNext,
  onPrevious,
}) => {
  const [portraitType, setPortraitType] = useState<
    "Normal" | "Hurt" | "Attack" | string
  >("Normal");
  const [isFlipped, setIsFlipped] = useState(false);
  const [isDead, setIsDead] = useState(false);
  const [isShaking, setIsShaking] = useState(false);
  const [lastActionTime, setLastActionTime] = useState(0);
  const [hoveredSkill, setHoveredSkill] = useState<SkillData | null>(null);

  const pokeData = getPokeData(pokemon.base_pokemon_name);
  const parsedPokemonName = useMemo(
    () => parsePokeNameForSprite(pokemon.base_pokemon_name),
    [pokemon.base_pokemon_name],
  );

  // Available portrait pools
  const NORMAL_VARIANTS = useMemo(
    () => ["Normal", "Shouting", "Determined", "Happy", "Inspired", "Joyous"],
    [],
  );
  const HURT_VARIANTS = useMemo(
    () => ["Hurt", "Dizzy", "Pain", "Sigh", "Stunned"],
    [],
  );

  useEffect(() => {
    // Initial state
    setIsDead(pokemon.IsDestroyed || (pokemon.HP?.current ?? 0) <= 0);

    // Set visual status to selected (shows arrow)
    pokemon.setVisualStatus("selected", 0xffffff);

    const onHurt = (isHurting: boolean) => {
      if (isHurting) {
        setIsShaking(true);
        // Pick random hurt variant
        const variant =
          HURT_VARIANTS[Math.floor(Math.random() * HURT_VARIANTS.length)] ??
          "Hurt";
        setPortraitType(variant);
        setLastActionTime(Date.now());
      } else {
        // Return to a normal variant immediately after hurt ends
        // This will be overridden by the idle loop eventually
        setPortraitType("Normal");
      }
    };

    const onAnimChange = (name: string, dir: Direction) => {
      // Update flip
      const isLeft = [Direction.W, Direction.NW, Direction.SW].includes(dir);
      setIsFlipped(isLeft);

      if (
        ["Attack", "Skill", "Punch", "Kick", "Slice", "Shoot", "SpAttack"].some(
          (t) => name.includes(t),
        )
      ) {
        setPortraitType("Attack");
        setLastActionTime(Date.now());
      }
    };

    const onFaint = () => {
      setIsDead(true);
      setPortraitType("Pain"); // Or specific faint portrait if available
    };

    pokemon.events.on("hurt", onHurt);
    pokemon.events.on("animation_change", onAnimChange);
    pokemon.events.on("faint", onFaint);

    return () => {
      pokemon.setVisualStatus("idle", 0);
      pokemon.events.off("hurt", onHurt);
      pokemon.events.off("animation_change", onAnimChange);
      pokemon.events.off("faint", onFaint);
    };
  }, [pokemon, HURT_VARIANTS]);

  // Idle Portrait Rotation Effect
  useEffect(() => {
    if (isDead) return;

    let timeoutId: any;

    const scheduleNextRotation = () => {
      // Random delay between 1s and 3s
      const delay = 1000 + Math.random() * 2000;

      timeoutId = setTimeout(() => {
        // Only change if no recent action (hurt/attack) happened in the last 500ms
        // and we aren't currently in a "locked" state like attacking
        if (Date.now() - lastActionTime > 500) {
          const variant =
            NORMAL_VARIANTS[
              Math.floor(Math.random() * NORMAL_VARIANTS.length)
            ] ?? "Normal";
          setPortraitType(variant);
        }
        scheduleNextRotation();
      }, delay);
    };

    scheduleNextRotation();

    return () => clearTimeout(timeoutId);
  }, [isDead, lastActionTime, NORMAL_VARIANTS]);

  useEffect(() => {
    if (isShaking) {
      const timer = setTimeout(() => setIsShaking(false), 500);
      return () => clearTimeout(timer);
    }
  }, [isShaking]);

  // Stats Calculation
  const stats = useMemo(() => {
    if (!battleLog) return { damageDone: 0, healingDone: 0, damageTaken: 0 };
    let damageDone = 0,
      healingDone = 0,
      damageTaken = 0;

    for (const [tickStr, tickStats] of Object.entries(battleLog.statsPerTick)) {
      if (Number(tickStr) > currentTick) break;
      const pStats = tickStats[pokemon.id];
      if (pStats) {
        damageDone += pStats.damage_done;
        healingDone += pStats.healing_done;
        damageTaken += pStats.damage_taken;
      }
    }
    return { damageDone, healingDone, damageTaken };
  }, [pokemon.id, battleLog, currentTick]);

  // Skill Status Calculation
  const skillsStatus = useMemo<SkillCooldownInfo[]>(() => {
    const defaultStatus = pokemon.Skills.map((s) => {
      const startsActive = s.data.starts_active === true;
      return {
        skill: s.data,
        isOnCooldown: !startsActive, // Skills that don't start active begin on cooldown
        remainingTicks: startsActive ? 0 : s.data.cooldown_ticks,
        remainingSeconds: startsActive
          ? 0
          : Math.ceil((s.data.cooldown_ticks * MS_PER_TICK) / 1000),
        totalCooldownTicks: s.data.cooldown_ticks,
      };
    });

    if (!battleLog) return defaultStatus;

    const member = [
      ...battleLog.teamA.members,
      ...battleLog.teamB.members,
    ].find((m) => m.battleData.id === pokemon.id);
    if (!member) return defaultStatus;

    return pokemon.Skills.map((clientSkill) => {
      const skillData = clientSkill.data;
      const actions = member.actions.filter(
        (a) =>
          a.type === "USE_SKILL" &&
          (a as any).skill.id === skillData.id &&
          a.starting_tick <= currentTick,
      );

      let isOnCooldown = false;
      let remainingTicks = 0;
      let totalCooldownTicks = skillData.cooldown_ticks;

      if (actions.length > 0) {
        const lastAction = actions[
          actions.length - 1
        ] as unknown as Battle_Action_UseSkill;
        if (lastAction) {
          const usedSkillData = lastAction.skill;
          const instanceCooldown =
            usedSkillData?.cooldown_ticks ?? skillData.cooldown_ticks;
          totalCooldownTicks = instanceCooldown;

          const lastUsageTick = lastAction.starting_tick;
          const offCooldownTick = lastUsageTick + instanceCooldown;
          if (currentTick < offCooldownTick) {
            isOnCooldown = true;
            remainingTicks = offCooldownTick - currentTick;
          }
        }
      } else {
        if (!skillData.starts_active) {
          const offCooldownTick = skillData.cooldown_ticks;
          if (currentTick < offCooldownTick) {
            isOnCooldown = true;
            remainingTicks = offCooldownTick - currentTick;
          }
        }
      }

      return {
        skill: skillData,
        isOnCooldown,
        remainingTicks,
        remainingSeconds: Math.ceil((remainingTicks * MS_PER_TICK) / 1000),
        totalCooldownTicks: totalCooldownTicks,
      };
    });
  }, [pokemon, battleLog, currentTick]);

  const hpPercent = Math.max(
    0,
    Math.min(100, ((pokemon.HP?.current ?? 0) / (pokemon.HP?.max ?? 1)) * 100),
  );
  const hpColor =
    hpPercent > 50
      ? "bg-pk-green-4"
      : hpPercent > 20
      ? "bg-pk-yellow-3"
      : "bg-pk-red-4";

  return (
    <div className="absolute bottom-0 left-0 w-full  flex items-end justify-center pointer-events-none z-[1000] ">
      <style>{`
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          25% { transform: translateX(-4px); }
          75% { transform: translateX(4px); }
        }
        @keyframes bob {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-3px); }
        }
        .animate-shake {
          animation: shake 0.2s cubic-bezier(.36,.07,.19,.97) both;
        }
        .animate-bob {
          animation: bob 1s ease-in-out infinite;
        }
      `}</style>

      {/* Navigation Arrows */}
      {onPrevious && (
        <button
          onClick={onPrevious}
          className="absolute bottom-32 left-4 pointer-events-auto bg-black/40 hover:bg-black/60 text-white w-12 h-12 rounded-full flex items-center justify-center border-2 border-white/20 backdrop-blur-sm transition-all active:scale-90 shadow-lg cursor-pointer"
        >
          <span className="text-2xl leading-none">◀</span>
        </button>
      )}
      {onNext && (
        <button
          onClick={onNext}
          className="absolute bottom-32 right-4 pointer-events-auto bg-black/40 hover:bg-black/60 text-white w-12 h-12 rounded-full flex items-center justify-center border-2 border-white/20 backdrop-blur-sm transition-all active:scale-90 shadow-lg cursor-pointer"
        >
          <span className="text-2xl leading-none">▶</span>
        </button>
      )}

      {/* Main HUD Container */}
      <div className="rounded-t-xl py-0.5 flex gap-2 shadow-2xl pointer-events-auto relative w-full items-end">
        {/* Left: Portrait */}
        <div
          className="flex flex-col justify-start items-start rounded-r-lg  gap-2 w-[105px]"
          style={{
            backgroundColor: `var(--color-pk-${pokeData?.types[0]?.toLocaleLowerCase()})`,
          }}
        >
          <div className="relative shrink-0 w-full">
            <div
              className={cn(
                "w-full h-20 bg-gradient-to-b from-gray-700 to-gray-900 rounded-lg  overflow-hidden shadow-inner transition-transform duration-100",
                isShaking && "animate-shake",
                isDead && "grayscale",
              )}
            >
              <div className={cn(!isDead && "animate-bob")}>
                <img
                  src={`${S3_BASE_URL}/pokemons/${parsedPokemonName}/portraits/${portraitType}.png`}
                  alt={pokemon.Name}
                  className={cn(
                    "w-full h-full object-contain pixelated scale-100 -translate-y-2 transition-transform duration-200",
                    isFlipped && "-scale-x-100",
                  )}
                  onError={(e) => {
                    // Fallback to Normal.png if variant doesn't exist
                    const target = e.target as HTMLImageElement;
                    const fallback = `${S3_BASE_URL}/pokemons/${parsedPokemonName}/portraits/Normal.png`;

                    // Prevent infinite loop if Normal.png is also missing
                    if (target.src !== fallback) {
                      // If we were trying to show a special variant and it failed,
                      // revert state to Normal so we don't keep trying to load the bad image
                      // on subsequent renders (though the img tag handles the immediate fallback)
                      target.src = fallback;
                    }
                  }}
                />
              </div>
            </div>
          </div>
          <div className="px-2 pb-1">
            <StrokedText
              text={capitalizeFirstLetter(pokemon.Name)}
              strokeWidth={7}
              fontSize={16}
            />
          </div>
        </div>

        {/* Middle: Info & Skills */}
        <div className="flex flex-col gap-2 grow ">
          {/* Header: Name & HP */}
          <div className="flex flex-col gap-1">
            {/* HP Bar */}
            <div className="w-full h-8 bg-gray-800  border-4 rounded-lg border-pk-gray-2 overflow-hidden relative">
              <div className="absolute top-2/5 left-1 -translate-y-1/2 ">
                🤍
              </div>

              <div className="flex justify-between items-baseline absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2">
                <span className="text-xs  text-pk-gray-1">
                  {Math.ceil(pokemon.HP?.current ?? 0)}{" "}
                  <span className="text-gray-500">/</span>{" "}
                  {pokemon.HP?.max ?? 0}
                </span>
              </div>

              <div
                className={`h-full ${hpColor} transition-all duration-200 ease-out`}
                style={{ width: `${hpPercent}%` }}
              />
              {/* Shield Bar visual placeholder if needed */}
              {pokemon.Shield.amount > 0 && (
                <div
                  className="absolute top-0 h-full bg-white/50 border-l border-white/20"
                  style={{
                    left: `${hpPercent}%`,
                    width: `${Math.min(
                      100 - hpPercent,
                      (pokemon.Shield.amount / (pokemon.HP?.max ?? 1)) * 100,
                    )}%`,
                  }}
                />
              )}
            </div>
          </div>

          {/* Skills Row */}
          <div className="grid grid-cols-3 gap-1 w-full ">
            {skillsStatus.map((info, idx) => (
              <SkillButton
                key={idx}
                info={info}
                onMouseEnter={() => setHoveredSkill(info.skill)}
                onMouseLeave={() => setHoveredSkill(null)}
                onClick={() =>
                  setHoveredSkill(
                    hoveredSkill?.id === info.skill.id ? null : info.skill,
                  )
                }
              />
            ))}
          </div>
        </div>

        {/* Right: Combat Stats */}
        <div className="flex flex-col justify-center gap-2 shrink-0 pr-2 ml-auto p-2 rounded-l-lg bg-pk-gray-2">
          <StatItem
            label="Damage"
            value={stats.damageDone}
            color="text-red-400"
          />
          <StatItem
            label="Healing"
            value={stats.healingDone}
            color="text-green-400"
          />
          <StatItem
            label="Taken"
            value={stats.damageTaken}
            color="text-orange-400"
          />
        </div>
      </div>

      {/* Skill Card Overlay */}
      {hoveredSkill && (
        <div
          className="fixed inset-0 z-[2000] flex items-center justify-center "
          onClick={() => setHoveredSkill(null)}
        >
          <div className="transform max-w-[270px]">
            <PokemonSkillCard
              skill={hoveredSkill}
              compact={false}
              isSelectable={false}
            />
          </div>
        </div>
      )}
    </div>
  );
};
